import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  ArrowUp,
  ChartLineUp,
  CheckCircle,
  Fire,
  MagnifyingGlass,
  PlayCircle,
  SealCheck,
  Sparkle,
  X,
} from "@phosphor-icons/react";

import {
  ApiError,
  loadCatalog,
  loadCatalogIndex,
  loadPromotionSiteIndex,
  loadPromotionSitePage,
  submitPromotionSiteSampleRequest,
} from "./api";
import sampleHandleExample from "./assets/tiktok-handle-example.png";
import {
  CATALOGS,
  isCatalogKey,
  type CatalogDefinition,
  type CatalogKey,
} from "./catalogs";
import type {
  PromotionSiteIndexPayload,
  PromotionSitePagePayload,
  PromotionSitePageSummary,
  PublicProduct,
} from "./types";

type Route =
  | { type: "opportunities" }
  | { type: "catalog"; catalog: CatalogKey }
  | { type: "site-page"; slug: string }
  | { type: "not-found" };

function resolveRoute(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/mx/oportunidades") return { type: "opportunities" };
  const catalog = clean.match(/^\/mx\/oportunidades\/([^/]+)$/);
  if (catalog && isCatalogKey(catalog[1])) {
    return { type: "catalog", catalog: catalog[1] };
  }
  if (catalog && /^[a-z0-9][a-z0-9-]{1,79}$/.test(catalog[1])) {
    return { type: "site-page", slug: catalog[1] };
  }
  return { type: "not-found" };
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function BrandHeader() {
  return (
    <header className="brand-header">
      <a className="brand-lockup" href="/mx/" aria-label="BJN México, inicio">
        <strong>BJN</strong>
        <span>MÉXICO</span>
      </a>
      <span className="brand-header__context">
        Oportunidades para creadores
      </span>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="app-footer">
      <div><strong>BJN</strong><span>BRIDGEJET NETWORK · MÉXICO</span></div>
      <p>Las selecciones pueden cambiar por versión. Confirma siempre la información vigente en TikTok Shop.</p>
      <small>BJN no representa a TikTok.</small>
    </footer>
  );
}

function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const updateVisibility = () => setVisible((current) => (
      window.scrollY > 320 || (current && window.scrollY > 1)
    ));
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    return () => window.removeEventListener("scroll", updateVisibility);
  }, []);
  return <button
    type="button"
    className="back-to-top"
    hidden={!visible}
    aria-label="Volver arriba"
    title="Volver arriba"
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
  ><ArrowUp weight="bold" /></button>;
}

function definitionForPage(slug: string, name: string): CatalogDefinition {
  if (slug === "tiktok-oficial") return {
    ...CATALOGS["moda-mujer"],
    title: name,
    shortTitle: name,
    accent: "#c92e24",
    softAccent: "#f5d9d7",
  };
  if (slug === "productos-virales") return { ...CATALOGS.virales, title: name, shortTitle: name };
  if (slug === "nuevas-oportunidades") return { ...CATALOGS.novedades, title: name, shortTitle: name };
  return {
    ...CATALOGS.novedades,
    title: name,
    shortTitle: name,
    description: "Selección de productos publicada por el equipo BJN México.",
  };
}

function CatalogMark({ slug }: { slug: string }) {
  if (slug === "productos-virales") return <Fire weight="fill" aria-hidden="true" />;
  if (slug === "tiktok-oficial") return <Sparkle weight="fill" aria-hidden="true" />;
  return <ChartLineUp weight="bold" aria-hidden="true" />;
}

function usePromotionSiteIndex() {
  const [data, setData] = useState<PromotionSiteIndexPayload | null>(null);
  useEffect(() => {
    let active = true;
    loadPromotionSiteIndex()
      .catch(async () => {
        const legacy = await loadCatalogIndex();
        return {
          meta: {
            market: "mx" as const,
            releaseId: "legacy-catalog-index",
            generatedAt: new Date(0).toISOString(),
            pageCount: legacy.catalogs.filter((item) => item.available).length,
            uniqueProductCount: 0,
          },
          pages: legacy.catalogs
            .filter((item) => item.available)
            .map((item, index) => ({
              name: CATALOGS[item.key].title,
              slug: item.key,
              order: index,
              productCount: item.productCount,
            })),
        };
      })
      .then((payload) => { if (active) setData(payload); })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, []);
  return data;
}

function CatalogCard({ page, index }: {
  page: PromotionSitePageSummary;
  index: number;
}) {
  const definition = definitionForPage(page.slug, page.name);
  return (
    <article
      className="opportunity-card"
      style={{
        "--catalog-accent": definition.accent,
        "--catalog-soft": definition.softAccent,
      } as React.CSSProperties}
    >
      <div className="opportunity-card__visual">
        <span className="opportunity-card__icon"><CatalogMark slug={page.slug} /></span>
        <span className="opportunity-card__index">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="opportunity-card__body">
        <p>{definition.eyebrow}</p>
        <h2>{definition.title}</h2>
        <span>{definition.description}</span>
        <ul>
          {definition.features.map((feature) => (
            <li key={feature}><CheckCircle weight="fill" /> {feature}</li>
          ))}
        </ul>
        <a
          className="card-link"
          href={`/mx/oportunidades/${page.slug}/`}
        >
          <span>
            {`Ver ${page.productCount} productos`}
          </span>
          <ArrowRight weight="bold" />
        </a>
      </div>
    </article>
  );
}

function OpportunitiesPage() {
  const index = usePromotionSiteIndex();
  useDocumentTitle("Oportunidades para creadores | BJN México");
  return (
    <div className="app-page">
      <BrandHeader />
      <main>
        <section className="opportunities-hero">
          <div className="opportunities-hero__copy">
            <p className="page-kicker"><span /> PARA CREADORES DE TIKTOK SHOP MÉXICO</p>
            <h1>Encuentra productos para tu próximo <em>gran contenido.</em></h1>
            <p>Explora selecciones BJN compartidas y publicadas mediante versiones confirmadas.</p>
          </div>
          <div className="opportunities-hero__stamp" aria-hidden="true">
            <small>DESCUBRE</small><strong>{index?.pages.length || 0}</strong><span>SELECCIONES</span>
          </div>
        </section>

        <section className="preview-notice" aria-label="Información sobre las selecciones">
          <SealCheck weight="fill" />
          <p><strong>Todos los accesos de producto son ShareLink.</strong> Un mismo PID reutiliza el mismo enlace BJN en las distintas selecciones.</p>
        </section>

        <section className="opportunity-grid" aria-label="Selecciones BJN">
          {(index?.pages || []).map((page, pageIndex) => (
            <CatalogCard key={page.slug} page={page} index={pageIndex} />
          ))}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function OfficialProductCard({ product, onRequestSample }: {
  product: PublicProduct;
  onRequestSample: (product: PublicProduct) => void;
}) {
  return (
    <article className="official-product-card">
      <div className="official-product-card__visual">
        <img src={product.image} alt={product.displayTitle || product.title} loading="lazy" decoding="async" />
      </div>
      <div className="official-product-card__identity">
        <div className="official-product-card__labels">
          {(product.sourceLabels || []).map((label) => <span key={label}>{label}</span>)}
        </div>
        <p>{product.category || "Producto oficial"}</p>
        <h3>{product.displayTitle || product.title}</h3>
      </div>
      <div className="official-product-card__commercial">
        <small>PRECIO GUÍA PARA LIVE FLASH SALE</small>
        <strong>{product.liveFlashSalePrice ? `${product.liveFlashSalePrice} MXN` : "Consulta en TikTok Shop"}</strong>
        <span>Precio final · Promotion Price <b>{product.promotionPrice ? `${product.promotionPrice} MXN` : product.price ? `$${product.price} MXN` : "Consulta en TikTok Shop"}</b></span>
      </div>
      <dl className="official-product-card__facts">
        <div><dt>PID</dt><dd>{product.id}</dd></div>
      </dl>
      <div className="official-product-card__actions">
        <a className="official-product-card__shop-link" href={product.shareLink}>
          <span>Agregar al escaparate</span><ArrowSquareOut weight="bold" />
        </a>
        <div className="official-product-card__secondary-actions">
          {product.referenceVideo ? <a href={product.referenceVideo} target="_blank" rel="noreferrer">
            <PlayCircle weight="fill" /><span>Video viral</span>
          </a> : null}
          <button type="button" onClick={() => onRequestSample(product)}>
            <span>Solicitar muestra</span>
          </button>
        </div>
      </div>
    </article>
  );
}

function CampaignProductCard({ product }: { product: PublicProduct }) {
  const sales = product.productSales === undefined
    ? "Sin dato"
    : new Intl.NumberFormat("es-MX", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(product.productSales);
  return <article className="campaign-product-card">
    <div className="campaign-product-card__visual">
      <img src={product.image} alt={product.displayTitle || product.title} loading="lazy" decoding="async" />
    </div>
    <div className="campaign-product-card__identity">
      <p>{product.category || "Otros"}</p>
      <h3>{product.displayTitle || product.title}</h3>
      {product.store ? <span>Vendido por {product.store}</span> : null}
      <div className="campaign-product-card__signals">
        <span><Sparkle weight="fill" /> {product.productRating || "Sin dato"} valoración</span>
        <span><ChartLineUp weight="bold" /> {sales} ventas</span>
      </div>
    </div>
    <div className="campaign-product-card__price">
      <small>PRECIO ACTUAL</small>
      <strong>{product.price ? `$${product.price} MXN` : "Consulta en TikTok Shop"}</strong>
    </div>
    <dl className="campaign-product-card__facts"><div><dt>PID</dt><dd>{product.id}</dd></div></dl>
    <a className="campaign-product-card__sample-link" href={product.shareLink}>
      <span>Solicitar muestra</span><ArrowSquareOut weight="bold" />
    </a>
  </article>;
}

function CategoryNavigation({
  categories,
  total,
  selected,
  onSelect,
  label,
  className = "",
}: {
  categories: Array<{ name: string; count: number }>;
  total: number;
  selected: string;
  onSelect: (category: string) => void;
  label: string;
  className?: string;
}) {
  return <nav className={`official-category-nav ${className}`.trim()} aria-label={label}>
    <button type="button" className={!selected ? "is-active" : ""} aria-pressed={!selected} onClick={() => onSelect("")}>Todos <span>{total}</span></button>
    {categories.map((category) => <button type="button" key={category.name} className={selected === category.name ? "is-active" : ""} aria-pressed={selected === category.name} onClick={() => onSelect(category.name)}>{category.name} <span>{category.count}</span></button>)}
  </nav>;
}

function SampleRequestDialog({ product, pageSlug, releaseId, onClose }: {
  product: PublicProduct;
  pageSlug: string;
  releaseId: string;
  onClose: () => void;
}) {
  const [handle, setHandle] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const clean = handle.trim();
    if (clean.startsWith("@")) {
      setError("Escribe tu usuario sin @.");
      return;
    }
    if (!/^[A-Za-z0-9._]{2,24}$/.test(clean)) {
      setError("Usa entre 2 y 24 letras, números, puntos o guiones bajos.");
      return;
    }
    if (!consent) {
      setError("Confirma el envío de tu usuario y el PID a BJN.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      await submitPromotionSiteSampleRequest({ handle: clean, pageSlug, pid: product.id, releaseId });
      setSubmitted(true);
    } catch (reason) {
      setError(reason instanceof ApiError && reason.status === 429
        ? "Has enviado varias solicitudes. Inténtalo de nuevo más tarde."
        : "No pudimos registrar la solicitud. Inténtalo de nuevo.");
    } finally {
      setPending(false);
    }
  };
  return <div className="sample-request-overlay" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="sample-request-dialog" role="dialog" aria-modal="true" aria-labelledby="sample-request-title">
      <button type="button" className="sample-request-dialog__close" aria-label="Cerrar" onClick={onClose}><X weight="bold" /></button>
      {submitted ? <div className="sample-request-success">
        <CheckCircle weight="fill" />
        <h2 id="sample-request-title">Solicitud recibida</h2>
        <p>BJN registró tu usuario y el PID para seguimiento. Esto no significa aprobación automática de la muestra.</p>
        <button type="button" onClick={onClose}>Cerrar</button>
      </div> : <form onSubmit={submit}>
        <p className="official-catalog-kicker">SOLICITUD DE MUESTRA</p>
        <h2 id="sample-request-title">Escribe tu usuario de TikTok</h2>
        <p>Usa el nombre que aparece debajo de tu perfil. No incluyas el símbolo @.</p>
        <div className="sample-request-dialog__example">
          <img src={sampleHandleExample} alt="Ejemplo de dónde encontrar el usuario de TikTok" />
          <span>En este ejemplo debes escribir <strong>s1mple937</strong>, sin @.</span>
        </div>
        <aside className="sample-request-dialog__showcase-note" aria-label="Paso previo para solicitar la muestra">
          <strong>Antes de enviar tu solicitud</strong>
          <span>Agrega primero este producto a tu escaparate de TikTok Shop.</span>
        </aside>
        <label className="sample-request-dialog__input">
          <span>Usuario de TikTok</span>
          <input autoFocus autoCapitalize="none" autoComplete="off" spellCheck={false} maxLength={24} value={handle} onChange={(event) => { setHandle(event.target.value); setError(null); }} />
        </label>
        <dl><div><dt>Producto</dt><dd>{product.displayTitle || product.title}</dd></div><div><dt>PID</dt><dd>{product.id}</dd></div></dl>
        <label className="sample-request-dialog__consent"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} /><span>Acepto enviar mi usuario de TikTok y este PID a BJN para gestionar la solicitud.</span></label>
        {error ? <p className="sample-request-dialog__error" role="alert">{error}</p> : null}
        <button className="sample-request-dialog__submit" type="submit" disabled={pending}>{pending ? "Enviando…" : "Enviar solicitud"}</button>
      </form>}
    </section>
  </div>;
}

function ProductCard({ product }: { product: PublicProduct }) {
  return (
    <article className="live-product-card">
      <div className="live-product-card__image">
        <img src={product.image} alt={product.displayTitle || product.title} loading="lazy" decoding="async" />
      </div>
      <div className="live-product-card__body">
        <p>{product.category}</p>
        <h3>{product.displayTitle || product.title}</h3>
        {product.store ? <span>Vendido por {product.store}</span> : null}
        {product.price ? <strong>MXN ${product.price}</strong> : null}
        <div className="live-product-card__signals">
          <span>PID {product.id}</span>
          <span>Consulta los detalles vigentes en TikTok Shop</span>
        </div>
        <a href={product.shareLink}>
          <span>Agregar al escaparate</span><ArrowSquareOut weight="bold" />
        </a>
      </div>
    </article>
  );
}

function LoadingState() {
  return <main className="state-page"><div className="state-loader"><i /><i /><i /></div><p>Preparando la selección…</p></main>;
}

function ErrorState({ title = "Selección no disponible", message }: {
  title?: string;
  message: string;
}) {
  return <main className="state-page"><span className="state-page__icon">!</span><h1>{title}</h1><p>{message}</p><a href="/mx/oportunidades/">Ver oportunidades BJN</a></main>;
}

function CatalogPage({ slug, legacyCatalog }: { slug: string; legacyCatalog?: CatalogKey }) {
  const [data, setData] = useState<PromotionSitePagePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sampleProduct, setSampleProduct] = useState<PublicProduct | null>(null);
  useEffect(() => {
    let active = true;
    const request = legacyCatalog
      ? loadCatalog(legacyCatalog).then((payload): PromotionSitePagePayload => ({
        meta: {
          market: "mx",
          releaseId: payload.meta.releaseId,
          generatedAt: payload.meta.generatedAt,
          slug: legacyCatalog,
          name: CATALOGS[legacyCatalog].title,
          productCount: payload.meta.productCount,
        },
        products: payload.products,
      }))
      : loadPromotionSitePage(slug);
    request
      .then((payload) => { if (active) setData(payload); })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof ApiError && reason.status === 404
          ? "Esta selección todavía no está publicada."
          : "No pudimos cargar los productos. Inténtalo de nuevo más tarde.");
      });
    return () => { active = false; };
  }, [legacyCatalog, slug]);
  const definition = definitionForPage(slug, data?.meta.name || "Selección BJN");
  const isOfficialPage = slug === "tiktok-oficial";
  const isCampaignPage = !legacyCatalog && !isOfficialPage;
  useDocumentTitle(`${data?.meta.name || definition.title} | BJN México`);
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of data?.products || []) {
      const category = String(product.category || "Otros");
      counts.set(category, (counts.get(category) || 0) + 1);
    }
    return [...counts].map(([name, count]) => ({ name, count }));
  }, [data]);
  const products = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es-MX");
    if (!data) return [];
    return data.products.filter((product) => {
      if (selectedCategory && product.category !== selectedCategory) return false;
      if (!normalized) return true;
      return [product.displayTitle, product.title, product.store, product.category, product.id]
        .join(" ")
        .toLocaleLowerCase("es-MX")
        .includes(normalized);
    });
  }, [data, query, selectedCategory]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <LoadingState />;
  const publishedDate = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/Mexico_City",
  }).format(new Date(data.meta.generatedAt));
  return (
    <div
      className={`app-page private-page${isOfficialPage ? " is-official-page" : ""}`}
      style={{
        "--catalog-accent": definition.accent,
        "--catalog-soft": definition.softAccent,
      } as React.CSSProperties}
    >
      <BrandHeader />
      <main>
        <section className={isOfficialPage ? "official-catalog-hero" : "private-catalog-hero"}>
          <a href="/mx/oportunidades/"><ArrowLeft weight="bold" /> Volver a oportunidades</a>
          {isOfficialPage ? <div className="official-catalog-hero__content">
            <p className="official-catalog-kicker">CATÁLOGO OFICIAL PARA CREADORES · MÉXICO</p>
            <h1>{definition.title}</h1>
            <p>{definition.description}</p>
            <div className="official-catalog-hero__trust"><SealCheck weight="fill" /> Selección oficial preparada para creadores</div>
            <dl className="official-catalog-hero__stats" aria-label="Resumen del catálogo oficial">
              <div><dt>{data.meta.productCount}</dt><dd>productos</dd></div>
              <div><dt>{data.meta.productCount}</dt><dd>enlaces listos</dd></div>
              <div><dt>MXN</dt><dd>precios publicados</dd></div>
            </dl>
            <small>Actualizado el {publishedDate}</small>
          </div> : <>
            <p className="page-kicker"><span /> {definition.eyebrow}</p>
            <h1>{definition.title}</h1>
            <p>{definition.description}</p>
            <div className="private-catalog-hero__meta">
              <strong>{data.meta.productCount}</strong>
              <span>productos · versión publicada</span>
            </div>
          </>}
        </section>
        <section className={`live-catalog${isOfficialPage ? " official-catalog" : ""}`}>
          {isOfficialPage ? <header className="official-catalog__toolbar">
            <div>
              <p className="official-catalog-kicker">SELECCIÓN 01</p>
              <h2>Productos oficiales de TikTok</h2>
              <span>Primero agrega a tu escaparate los productos que te gusten y después haz clic en “Solicitar muestra”.</span>
            </div>
            <b>{products.length} {products.length === 1 ? "producto" : "productos"}</b>
            <label className="catalog-search">
              <MagnifyingGlass weight="bold" />
              <span className="sr-only">Buscar productos</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Busca producto, tienda o PID"
              />
            </label>
            <CategoryNavigation categories={categories} total={data.meta.productCount} selected={selectedCategory} onSelect={setSelectedCategory} label="Filtrar productos oficiales por categoría" />
          </header> : <>
            <label className="catalog-search">
              <MagnifyingGlass weight="bold" />
              <span className="sr-only">Buscar productos</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Busca producto, tienda o PID"
              />
            </label>
            {isCampaignPage ? <CategoryNavigation categories={categories} total={data.meta.productCount} selected={selectedCategory} onSelect={setSelectedCategory} label="Filtrar productos por categoría" className="campaign-category-nav" /> : null}
            <p className="catalog-result-count">
              {products.length} {products.length === 1 ? "producto" : "productos"}
            </p>
          </>}
          {products.length
            ? <div className={`live-product-grid${isOfficialPage ? " official-product-grid" : isCampaignPage ? " campaign-product-grid" : ""}`}>{products.map((product) => isOfficialPage
              ? <OfficialProductCard key={product.id} product={product} onRequestSample={setSampleProduct} />
              : isCampaignPage
                ? <CampaignProductCard key={product.id} product={product} />
                : <ProductCard key={product.id} product={product} />)}</div>
            : <div className="catalog-empty"><MagnifyingGlass weight="bold" /><h2>No encontramos productos</h2><p>Prueba con otra búsqueda.</p></div>}
        </section>
      </main>
      {sampleProduct ? <SampleRequestDialog product={sampleProduct} pageSlug={slug} releaseId={data.meta.releaseId} onClose={() => setSampleProduct(null)} /> : null}
      <SiteFooter />
    </div>
  );
}

function NotFoundPage() {
  useDocumentTitle("Página no encontrada | BJN México");
  return <ErrorState title="Página no encontrada" message="La dirección que abriste no está disponible." />;
}

export function App() {
  const route = resolveRoute(window.location.pathname);
  let page: React.ReactNode;
  if (route.type === "opportunities") page = <OpportunitiesPage />;
  else if (route.type === "catalog") page = <CatalogPage slug={route.catalog} legacyCatalog={route.catalog} />;
  else if (route.type === "site-page") page = <CatalogPage slug={route.slug} />;
  else page = <NotFoundPage />;
  return <>{page}<BackToTopButton /></>;
}
